import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { AzureTicketCreatorConfig, AzureTicketDraft } from '@evinced-private/ui-common';

import { useIsJiraWriteBackEnabled } from 'src/common/hooks/useIsJiraWriteBackEnabled';
import { getJiraAccessToken } from 'src/common/services/api/LocalStorageApi';
import { buildAzureTicketCreatorConfig } from 'src/common/services/azureTicketCreatorConfig';

import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { formatIssueForExport } from '../jira-issue-creator/helpers/jira-export-helper';

import { buildAzureTicketDraft } from './helpers/azure-export-helper';
import { JiraCloudModule } from './JiraCloudModule';
import { ITicketsSystems } from './TicketsSystems.types';
import { JIRA_DESCRIPTION_PLATFORM } from './TicketSystems.constants';

interface IUseTicketsSystems {
	enableAzureIntegration: boolean;
	enableAzureBypassRule: boolean;
	azureConfig: AzureTicketCreatorConfig;
	azureTicket: AzureTicketDraft;
	isJiraConnected: boolean;
	exportToJira: () => void;
	renderJiraModal: () => ReactElement | null;
}

export const useTicketsSystems = ({
	issue,
	type = JIRA_DESCRIPTION_PLATFORM.WEB,
	onTicketCreated
}: ITicketsSystems): IUseTicketsSystems => {
	const isJiraWriteBackEnabled = useIsJiraWriteBackEnabled();
	const { configuration } = useConfiguration();
	const enableAzureIntegration = configuration?.PLATFORM?.CONFIG?.ENABLE_AZURE_INTEGRATION ?? false;
	const enableAzureBypassRule = configuration?.PLATFORM?.CONFIG?.ENABLE_AZURE_BYPASS_RULE ?? false;
	const [isJiraConnected, setJiraConnected] = useState(false);
	const [issueToExport, setIssueToExport] = useState(null);
	const [isJiraModalOpened, setJiraModalOpened] = useState(false);
	const issueNote = typeof issue?.description === 'string' ? issue.description : undefined;

	const exportToJira = useCallback((): void => {
		setIssueToExport(formatIssueForExport(issue, issueNote, type));
		setJiraModalOpened(true);
	}, [issue, issueNote, type]);

	const checkIfJiraConnected = useCallback((): void => {
		const jiraToken = getJiraAccessToken();
		setJiraConnected(!!jiraToken);
	}, []);

	useEffect(() => {
		checkIfJiraConnected();
		window.addEventListener('storage', checkIfJiraConnected);

		return () => {
			window.removeEventListener('storage', checkIfJiraConnected);
		};
	}, [checkIfJiraConnected]);

	// Per-tenant Azure OAuth scope from central config overrides the env default (falls back to it,
	// then to the module's Azure DevOps default). Memoized so the module's provider resolves once.
	const azureConfig = useMemo(
		() => buildAzureTicketCreatorConfig(configuration?.PLATFORM?.CONFIG?.AZURE_SCOPES),
		[configuration?.PLATFORM?.CONFIG?.AZURE_SCOPES]
	);

	// Map the app's Report onto the azure-ticket-creator module's host-agnostic input contract.
	const azureTicket = useMemo<AzureTicketDraft>(
		() => buildAzureTicketDraft(issue, issueNote),
		[issue, issueNote]
	);

	const renderJiraModal = (): ReactElement | null =>
		issueToExport && isJiraModalOpened ? (
			<JiraCloudModule
				issueToExport={issueToExport}
				setJiraModalOpened={setJiraModalOpened}
				isJiraModalOpened={isJiraModalOpened}
				onTicketCreated={isJiraWriteBackEnabled ? onTicketCreated : undefined}
			/>
		) : null;

	return {
		enableAzureIntegration,
		enableAzureBypassRule,
		azureConfig,
		azureTicket,
		isJiraConnected,
		exportToJira,
		renderJiraModal
	};
};
